<div class="bep-shell">
  <header class="bep-header">
    <div class="bep-header__copy">
      <p class="bep-header__eyebrow">ReeWork · Bulk upload</p>
      <h2 class="bep-header__title">Review ingredients before save</h2>
      <p class="bep-header__sub">
        CSV values that don’t match master dropdowns are left blank — fill those manually before saving.
      </p>
    </div>
    <div class="bep-header__actions">
      <label class="bep-bulk-status">
        <span class="bep-bulk-status__label">Bulk status</span>
        <ng-select
          class="ingredient-page-select ingredient-page-select-panel bep-select--status"
          [items]="statusOptions"
          bindLabel="label"
          bindValue="value"
          [(ngModel)]="bulkStatus"
          (ngModelChange)="applyBulkStatus($event)"
          [clearable]="false"
          [searchable]="false"
          appendTo="body">
        </ng-select>
      </label>
      <button type="button" class="bep-icon-btn" (click)="activeModal.dismiss('Cross click')" aria-label="Close">
        <i data-lucide="x"></i>
      </button>
    </div>
  </header>

  <div class="bep-body">
    <article class="bep-card" *ngFor="let item of pagedIngredients; let i = index; trackBy: trackByIngredient">
      <div class="bep-card__top">
        <div class="bep-card__identity">
          <div class="bep-image" (click)="fileInput.click()" title="Change image">
            <input #fileInput type="file" class="d-none" accept="image/*" (change)="onImageUpload($event, item)" />
            <img *ngIf="item.imagePreview" [src]="item.imagePreview" alt="" />
            <div *ngIf="!item.imagePreview" class="bep-image__empty">
              <i data-lucide="image-plus"></i>
              <span>Add photo</span>
            </div>
            <span class="bep-image__hint">Change</span>
          </div>

          <div class="bep-card__title-block">
            <div class="bep-card__meta">
              <span class="bep-chip bep-chip--muted">#{{ previewPageStart + i + 1 }}</span>
            </div>
            <input class="bep-name-input" type="text" [(ngModel)]="item.name" placeholder="Ingredient name" />
            <input class="bep-sci-input" type="text" [(ngModel)]="item.scientificName" placeholder="Scientific name" />
          </div>
        </div>

        <div class="bep-card__toolbar">
          <ng-select
            class="ingredient-page-select ingredient-page-select-panel bep-select--status"
            [items]="statusOptions"
            bindLabel="label"
            bindValue="value"
            [(ngModel)]="item.status"
            [clearable]="false"
            [searchable]="false"
            appendTo="body">
          </ng-select>
          <button type="button" class="bep-icon-btn bep-icon-btn--danger" (click)="deleteRow(previewPageStart + i)" title="Remove from batch">
            <i data-lucide="trash-2"></i>
          </button>
        </div>
      </div>

      <div class="bep-sections">
        <div class="bep-warn" *ngIf="hasUnresolvedCsv(item)">
          <i data-lucide="alert-circle"></i>
          <span>
            Could not auto-match from CSV:
            <ng-container *ngIf="item._csvUnresolved?.dataSource"> Data source “{{ item._csvUnresolved.dataSource }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.foodGroup"> · Food group “{{ item._csvUnresolved.foodGroup }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.foodGroupFAO"> · FAO “{{ item._csvUnresolved.foodGroupFAO }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.season"> · Season “{{ item._csvUnresolved.season }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.category"> · Category “{{ item._csvUnresolved.category }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.mealType"> · Meal type “{{ item._csvUnresolved.mealType }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.occasion"> · Occasion “{{ item._csvUnresolved.occasion }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.mealPreferenceTaxonomy"> · Meal preference “{{ item._csvUnresolved.mealPreferenceTaxonomy }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.dietaryPreference"> · Dietary preference “{{ item._csvUnresolved.dietaryPreference }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.healthColorCode"> · Health color “{{ item._csvUnresolved.healthColorCode }}”</ng-container>
            <ng-container *ngIf="item._csvUnresolved?.rawMaterialType"> · Material type “{{ item._csvUnresolved.rawMaterialType }}”</ng-container>
            — please select manually.
          </span>
        </div>

        <section class="bep-section">
          <h3 class="bep-section__title">Basics</h3>
          <div class="bep-grid bep-grid--2">
            <label class="bep-field">
              <span>Description</span>
              <textarea rows="2" [(ngModel)]="item.description" placeholder="Short description"></textarea>
            </label>
            <div class="bep-grid bep-grid--2 bep-grid--nested">
              <label class="bep-field">
                <span>Dietary preference</span>
                <ng-select
                  class="ingredient-page-select ingredient-page-select-panel"
                  [items]="dietaryPreferenceOptions"
                  bindLabel="name"
                  bindValue="name"
                  [(ngModel)]="item.mealPreference"
                  [clearable]="true"
                  placeholder="Select"
                  appendTo="body">
                </ng-select>
              </label>
              <label class="bep-field">
                <span>Health color code</span>
                <ng-select
                  class="ingredient-page-select ingredient-page-select-panel"
                  [items]="healthColorCodeOptions"
                  bindLabel="name"
                  bindValue="id"
                  [(ngModel)]="item.healthColorCodeId"
                  [clearable]="true"
                  placeholder="Select"
                  appendTo="body">
                </ng-select>
              </label>
              <label class="bep-field">
                <span>Glycemic index</span>
                <ng-select
                  class="ingredient-page-select ingredient-page-select-panel"
                  [items]="glycemicIndexOptions"
                  bindLabel="name"
                  bindValue="name"
                  [(ngModel)]="item.glycemicIndex"
                  [clearable]="true"
                  placeholder="Select"
                  appendTo="body">
                </ng-select>
              </label>
              <label class="bep-field">
                <span>INS number</span>
                <input type="text" [(ngModel)]="item.insNumber" placeholder="—" />
              </label>
              <label class="bep-field">
                <span>State of product</span>
                <ng-select
                  class="ingredient-page-select ingredient-page-select-panel"
                  [items]="stateOfProductOptions"
                  bindLabel="label"
                  bindValue="value"
                  [(ngModel)]="item.stateOfProduct"
                  [clearable]="false"
                  placeholder="Solid / Liquid"
                  appendTo="body">
                </ng-select>
              </label>
            </div>
          </div>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Classification</h3>
          <div class="bep-grid bep-grid--4">
            <label class="bep-field">
              <span>Data source</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="dataSourceOptions"
                bindLabel="name"
                bindValue="id"
                [(ngModel)]="item.dataSource"
                (change)="onDataSourceChange(item)"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Material type</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="rawMaterialTypeOptions"
                bindLabel="name"
                bindValue="id"
                [(ngModel)]="item.rawTypeId"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Food group(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="foodGroups"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.foodGroupIds"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>FAO food group(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="foodGroupsFAO"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.foodGroupFAOIds"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Season(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="seasons"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [ngModel]="item.seasonIds"
                (ngModelChange)="onSeasonSelectionChange(item, $event)"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Category</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="categories"
                bindLabel="name"
                bindValue="id"
                [(ngModel)]="item.itemCategoryId"
                [clearable]="true"
                placeholder="Select Item Category"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Meal Type(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="mealTypes"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.mealTypeIds"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Occasion(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="occasions"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.occasionIds"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Meal Preference(s)</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="mealPreferences"
                bindLabel="name"
                bindValue="id"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.mealPreferenceIds"
                [clearable]="true"
                placeholder="Select"
                appendTo="body">
              </ng-select>
            </label>
          </div>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Names</h3>
          <div class="bep-grid bep-grid--3">
            <label class="bep-field">
              <span>Also Known As</span>
              <input type="text" [(ngModel)]="item.aliases" placeholder="Comma or | separated" />
            </label>
            <label class="bep-field">
              <span>Common names</span>
              <input type="text" [(ngModel)]="item.commonName" placeholder="Comma or | separated" />
            </label>
            <label class="bep-field">
              <span>Regional names</span>
              <input type="text" [(ngModel)]="item.regionalNames" placeholder="Comma or | separated" />
            </label>
          </div>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Allergens &amp; intolerances</h3>
          <div class="bep-grid bep-grid--2">
            <label class="bep-field">
              <span>Allergens · contains</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="allergensContains"
                bindLabel="name"
                [multiple]="true"
                [(ngModel)]="item.allergens.contains"
                [clearable]="true"
                placeholder="None"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Allergens · free</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="allergensFree"
                bindLabel="name"
                [multiple]="true"
                [(ngModel)]="item.allergens.free"
                [clearable]="true"
                placeholder="None"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Intolerances · contains</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="intolerancesContains"
                bindLabel="name"
                [multiple]="true"
                [(ngModel)]="item.intolerances.contains"
                [clearable]="true"
                placeholder="None"
                appendTo="body">
              </ng-select>
            </label>
            <label class="bep-field">
              <span>Intolerances · free</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="intolerancesFree"
                bindLabel="name"
                [multiple]="true"
                [(ngModel)]="item.intolerances.free"
                [clearable]="true"
                placeholder="None"
                appendTo="body">
              </ng-select>
            </label>
          </div>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Nutrients</h3>
          <p class="bep-section__hint">Select nutrient groups, then manage tags. Unmatched CSV groups are ignored.</p>
          <div class="bep-grid bep-grid--2">
            <label class="bep-field">
              <span>Nutrients group</span>
              <ng-select
                class="ingredient-page-select ingredient-page-select-panel"
                [items]="nutrientGroups"
                bindLabel="name"
                [compareWith]="compareNutrientGroup"
                [multiple]="true"
                [closeOnSelect]="false"
                [(ngModel)]="item.nutrientGroups"
                (ngModelChange)="onNutrientGroupChange(item)"
                [clearable]="true"
                placeholder="Select nutrients group"
                appendTo="body">
              </ng-select>
            </label>
            <div class="bep-field">
              <span>Nutrients tags</span>
              <button
                type="button"
                class="bep-tag-btn"
                (click)="viewNutrientTags(item)"
                [disabled]="!item.nutrientGroups?.length">
                <i data-lucide="tags"></i>
                <span>{{ item.nutrientGroups?.length ? 'Manage tags' : 'Select group first' }}</span>
                <em *ngIf="getNutrientTagCount(item) > 0">{{ getNutrientTagCount(item) }}</em>
              </button>
            </div>
          </div>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Macros <span>(per 100g / 100ml)</span></h3>
          <div class="bep-macros">
            <label class="bep-macro">
              <span>Calories</span>
              <input type="number" step="any" [(ngModel)]="item._macros.Calories" />
            </label>
            <label class="bep-macro">
              <span>Carbs</span>
              <input type="number" step="any" [(ngModel)]="item._macros.Carbohydrate" />
            </label>
            <label class="bep-macro">
              <span>Protein</span>
              <input type="number" step="any" [(ngModel)]="item._macros.Protein" />
            </label>
            <label class="bep-macro">
              <span>Fat</span>
              <input type="number" step="any" [(ngModel)]="item._macros.Fat" />
            </label>
            <label class="bep-macro">
              <span>Fibre</span>
              <input type="number" step="any" [(ngModel)]="item._macros.Fibre" />
            </label>
          </div>
          <div class="bep-field" style="margin-top: 12px;">
            <button type="button" class="bep-tag-btn" (click)="viewMicronutrients(item)">
              <i data-lucide="flask-conical"></i>
              <span>Micronutrients</span>
              <em *ngIf="getMicronutrientCount(item) > 0">{{ getMicronutrientCount(item) }}</em>
            </button>
          </div>
        </section>

        <section class="bep-section" *ngIf="item.fssaiNutrientTagNames?.length || item.stateOfProduct">
          <h3 class="bep-section__title">
            FSSAI Nutrient Tags
            <span *ngIf="item.stateOfProduct">
              (cut-offs per {{ item.stateOfProduct === 'Liquid' ? '100ml Liquid' : '100g Solid' }})
            </span>
          </h3>
          <p class="bep-section__hint">
            Tags from file are shown below. On save they are recalculated from nutrition + State of Product (same as Add Ingredient).
          </p>
          <div class="bep-uom-preview" *ngIf="item.fssaiNutrientTagNames?.length; else noFssaiTags">
            <span *ngFor="let tag of item.fssaiNutrientTagNames">{{ tag }}</span>
          </div>
          <ng-template #noFssaiTags>
            <p class="bep-section__hint">No FSSAI tags in file — will be generated on save when nutrition values match cut-offs.</p>
          </ng-template>
        </section>

        <section class="bep-section">
          <h3 class="bep-section__title">Unit &amp; Measurement</h3>
          <p class="bep-section__hint">Serving sizes with macros calculated from per-100g values above.</p>
          <div class="bep-field">
            <button type="button" class="bep-tag-btn" (click)="viewUoms(item)">
              <i data-lucide="ruler"></i>
              <span>Manage UOMs</span>
              <em *ngIf="getUomCount(item) > 0">{{ getUomCount(item) }}</em>
            </button>
            <div class="bep-uom-preview" *ngIf="getUomCount(item) > 0">
              <span *ngFor="let uom of item.ingredientUnitOfMeasurements">
                {{ uom.size || '-' }} · {{ uom.unit || uom.type || 'Serving' }}
                <ng-container *ngIf="getUomAmount(uom)"> · {{ getUomAmount(uom) }}g</ng-container>
              </span>
            </div>
          </div>
        </section>
      </div>
    </article>

    <div class="bep-empty" *ngIf="!parsedIngredients?.length">
      <i data-lucide="inbox"></i>
      <p>No ingredients left to upload.</p>
    </div>
  </div>

  <footer class="bep-footer">
    <div class="bep-footer__status">
      <span class="bep-count">{{ parsedIngredients.length }} to upload</span>
      <div class="bep-pager" *ngIf="parsedIngredients.length > previewPageSize">
        <button type="button" class="bep-btn bep-btn--ghost" [disabled]="previewPage <= 1" (click)="prevPreviewPage()">Prev</button>
        <span class="bep-pager__label">Page {{ previewPage }} / {{ previewTotalPages }}</span>
        <button type="button" class="bep-btn bep-btn--ghost" [disabled]="previewPage >= previewTotalPages" (click)="nextPreviewPage()">Next</button>
      </div>
    </div>
    <div class="bep-footer__actions">
      <button type="button" class="bep-btn bep-btn--ghost" (click)="activeModal.dismiss('Cancel click')">
        Cancel
      </button>
      <button type="button" class="bep-btn bep-btn--primary" [disabled]="!parsedIngredients.length" (click)="save()">
        <i data-lucide="check"></i>
        Confirm &amp; Save
      </button>
    </div>
  </footer>
</div>

<ng-template #micronutrientsModal let-modal>
  <div class="micro-modal">
    <div class="micro-modal__header">
      <h4 class="micro-modal__title">Micronutrients</h4>
      <button type="button" class="micro-modal__close-btn" aria-label="Close" (click)="modal.dismiss('Cross click')">
        <i data-lucide="x"></i>
      </button>
    </div>
    <div class="micro-modal__body">
      <div class="micro-table-container">
        <table class="micro-table">
          <thead>
            <tr>
              <th>Nutrient</th>
              <th>Type</th>
              <th>Value</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let micro of selectedMicronutrientsList">
              <td class="micro-table__name">{{ micro.nutrient }}</td>
              <td><span class="micro-table__badge">{{ micro.nutrientType }}</span></td>
              <td>
                <input type="number" [(ngModel)]="micro.amount" class="micro-table__input" />
              </td>
              <td class="micro-table__unit">{{ micro.unit || '-' }}</td>
            </tr>
            <tr *ngIf="!selectedMicronutrientsList || selectedMicronutrientsList.length === 0">
              <td colspan="4" class="micro-table__empty">No micronutrients found for this data source.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="micro-modal__footer">
      <button type="button" class="btn btn--primary micro-modal__done-btn" (click)="saveMicronutrientsAndClose(modal)">
        Done
      </button>
    </div>
  </div>
</ng-template>

<ng-template #uomModal let-modal>
  <div class="bep-uom-modal">
    <div class="bep-uom-modal__header">
      <div>
        <h4 class="bep-uom-modal__title">Unit &amp; Measurement</h4>
        <p class="bep-uom-modal__subtitle" *ngIf="selectedUomItem">
          {{ selectedUomItem.name || selectedUomItem.ingredientName || 'Ingredient' }}
        </p>
      </div>
      <button type="button" class="bep-uom-modal__close" aria-label="Close" (click)="modal.dismiss('Cross click')">
        <i data-lucide="x"></i>
      </button>
    </div>
    <div class="bep-uom-modal__body">
      <div class="bep-uom-table-wrap">
        <table class="bep-uom-table">
          <thead>
            <tr>
              <th class="col-idx">#</th>
              <th class="col-size">Serving Size</th>
              <th class="col-measure">Serving Measure</th>
              <th class="col-amount">Amount</th>
              <th class="col-unit">Unit</th>
              <th class="col-macro">Calories</th>
              <th class="col-macro">Carbs</th>
              <th class="col-macro">Protein</th>
              <th class="col-macro">Fat</th>
              <th class="col-macro">Fibre</th>
              <th class="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let uom of selectedUomItem?.ingredientUnitOfMeasurements; let i = index">
              <td class="col-idx">{{ i + 1 }}</td>
              <td class="col-size">
                <ng-select
                  [items]="servingSizeOptions"
                  bindLabel="name"
                  bindValue="value"
                  placeholder="Size"
                  [(ngModel)]="uom.size"
                  (ngModelChange)="onSizeChange(uom)"
                  [clearable]="true"
                  [searchable]="true"
                  class="ingredient-page-select ingredient-page-select-panel bep-uom-select"
                  appendTo="body">
                </ng-select>
              </td>
              <td class="col-measure">
                <ng-select
                  [items]="servingMeasureOptions"
                  bindLabel="name"
                  bindValue="value"
                  placeholder="Measure"
                  [(ngModel)]="uom.unit"
                  (ngModelChange)="onUomMeasureChange(uom)"
                  [clearable]="true"
                  [searchable]="true"
                  class="ingredient-page-select ingredient-page-select-panel bep-uom-select"
                  appendTo="body">
                </ng-select>
              </td>
              <td class="col-amount">
                <input
                  type="number"
                  class="bep-uom-input"
                  [(ngModel)]="uom.quantity"
                  (ngModelChange)="onUomAmountChange(uom)"
                  placeholder="0"
                  min="0"
                  step="any" />
              </td>
              <td class="col-unit">
                <ng-select
                  [items]="unitOptions"
                  bindLabel="name"
                  bindValue="value"
                  placeholder="Unit"
                  [(ngModel)]="uom.type"
                  (ngModelChange)="onUomUnitChange(uom)"
                  [clearable]="true"
                  [searchable]="true"
                  class="ingredient-page-select ingredient-page-select-panel bep-uom-select"
                  appendTo="body">
                </ng-select>
              </td>
              <td class="col-macro">{{ calculateUomMacro('Calories', getUomAmount(uom)) }}</td>
              <td class="col-macro">{{ calculateUomMacro('Carbohydrate', getUomAmount(uom)) }}</td>
              <td class="col-macro">{{ calculateUomMacro('Protein', getUomAmount(uom)) }}</td>
              <td class="col-macro">{{ calculateUomMacro('Fat', getUomAmount(uom)) }}</td>
              <td class="col-macro">{{ calculateUomMacro('Fibre', getUomAmount(uom)) }}</td>
              <td class="col-action">
                <button type="button" class="bep-uom-delete" (click)="removeUomRow(i)" title="Remove">
                  <i data-lucide="trash-2"></i>
                </button>
              </td>
            </tr>
            <tr *ngIf="!selectedUomItem?.ingredientUnitOfMeasurements?.length">
              <td colspan="11" class="bep-uom-empty">No Unit &amp; Measurement entries added yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="bep-uom-modal__footer">
      <button type="button" class="bep-btn bep-btn--ghost" (click)="addUomRow()">
        <i data-lucide="plus"></i>
        Add Unit
      </button>
      <button type="button" class="bep-btn bep-btn--primary" (click)="modal.close()">Done</button>
    </div>
  </div>
</ng-template>

<ng-template #nutrientTagsModal let-modal>
  <div class="micro-modal nutrient-tags-modal">
    <div class="micro-modal__header">
      <div>
        <h4 class="micro-modal__title">Nutrient Tags</h4>
        <p class="nutrient-tags-modal__subtitle" *ngIf="selectedNutrientTagsItem">
          {{ selectedNutrientTagsItem.name || selectedNutrientTagsItem.ingredientName || 'Ingredient' }}
        </p>
      </div>
      <button type="button" class="micro-modal__close-btn" aria-label="Close" (click)="modal.dismiss('Cross click')">
        <i data-lucide="x"></i>
      </button>
    </div>
    <div class="micro-modal__body nutrient-tags-modal__body">
      <div class="nutrient-tag-chip-list" *ngIf="getSelectedTagsForModal().length; else noNutrientTags">
        <span class="nutrient-tag-chip" *ngFor="let tag of getSelectedTagsForModal()">
          <span class="nutrient-tag-chip__label">{{ tag.tagName || tag.name }}</span>
          <button type="button" class="nutrient-tag-chip__remove" aria-label="Remove tag" (click)="removeNutrientTag(tag)">
            <i data-lucide="x"></i>
          </button>
        </span>
      </div>
      <ng-template #noNutrientTags>
        <div class="nutrient-tags-modal__empty">No tags selected yet.</div>
      </ng-template>
      <div class="nutrient-tags-modal__add" *ngIf="selectedNutrientTagOptions.length">
        <label class="nutrient-tags-modal__add-label">Add tag</label>
        <ng-select
          [items]="selectedNutrientTagOptions"
          bindLabel="tagName"
          placeholder="Select tag to add"
          [(ngModel)]="nutrientTagToAdd"
          (change)="onAddNutrientTag($event)"
          [clearable]="true"
          class="ingredient-page-select ingredient-page-select-panel"
          appendTo="body">
        </ng-select>
      </div>
    </div>
    <div class="micro-modal__footer">
      <button type="button" class="btn btn--primary micro-modal__done-btn" (click)="modal.close()">Done</button>
    </div>
  </div>
</ng-template>
